import { useState, useEffect, useRef, useCallback } from 'react';
import { calculateQuote } from './utils/pricing';
import { logEvent } from './utils/analytics';
import pricingConfig from '../pricing.config.json';
import './App.css';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

// Debug logging
console.log('=== GOOGLE MAPS DEBUG ===');
console.log('API Key exists:', !!GOOGLE_MAPS_API_KEY);
console.log('API Key length:', GOOGLE_MAPS_API_KEY?.length);
console.log('API Key starts with AIza:', GOOGLE_MAPS_API_KEY?.startsWith('AIza'));
console.log('Full API Key:', GOOGLE_MAPS_API_KEY); // Remove this line after debugging!

function App() {
  const [step, setStep] = useState(1);
  const [address, setAddress] = useState('');
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [zipCode, setZipCode] = useState('');
  const [manualZip, setManualZip] = useState('');
  const [mapLoaded, setMapLoaded] = useState(false);
  const [polygon, setPolygon] = useState(null);
  const [area, setArea] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [quote, setQuote] = useState(null);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  
  // Options state
  const [thickness, setThickness] = useState('4');
  const [reinforcement, setReinforcement] = useState('none');
  const [finish, setFinish] = useState('broom');
  const [tint, setTint] = useState('none');
  const [cosmetics, setCosmetics] = useState({});

  // Form state
  const [leadForm, setLeadForm] = useState({
    name: '',
    phone: '',
    email: '',
    notes: '',
    timeline: 'asap'
  });

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const autocompleteRef = useRef(null);
  const searchInputRef = useRef(null);
  const drawingManagerRef = useRef(null);
  const polygonRef = useRef(null);

  // Load Google Maps script
  useEffect(() => {
    if (window.google?.maps?.places) {
      console.log('Google Maps already loaded');
      return;
    }

    if (!GOOGLE_MAPS_API_KEY) {
      console.error('❌ GOOGLE_MAPS_API_KEY is not defined!');
      alert('ERROR: Google Maps API key not found. Check your .env file.');
      return;
    }

    console.log('Loading Google Maps with key:', GOOGLE_MAPS_API_KEY.substring(0, 10) + '...');

    // Use callback method for more reliable loading
    window.initGoogleMaps = () => {
      console.log('✅ Google Maps loaded and initialized');
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,drawing,geometry&callback=initGoogleMaps`;
    script.async = true;
    script.defer = true;
    
    script.onerror = () => {
      console.error('❌ Failed to load Google Maps script');
      alert('Failed to load Google Maps. Check your API key and internet connection.');
    };
    
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
      delete window.initGoogleMaps;
    };
  }, []);

  // Initialize autocomplete - wait for Google Maps to be fully ready
  useEffect(() => {
    if (autocompleteRef.current) return;

    // Poll until Google Maps Places is fully loaded
    const checkGoogleReady = () => {
      if (window.google?.maps?.places?.Autocomplete && searchInputRef.current) {
        try {
          console.log('Initializing autocomplete...');
          
          const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current, {
            componentRestrictions: { country: 'us' },
            fields: ['address_components', 'formatted_address', 'geometry', 'name']
          });

          autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            
            if (!place.geometry) {
              alert('Please select a valid address from the dropdown');
              return;
            }

            const zip = place.address_components?.find(
              comp => comp.types.includes('postal_code')
            )?.long_name || '';

            setSelectedPlace(place);
            setAddress(place.formatted_address);
            setZipCode(zip);
            setManualZip(zip);
            
            logEvent('address_selected', { 
              address: place.formatted_address,
              zip 
            });
          });

          autocompleteRef.current = autocomplete;
          console.log('✅ Autocomplete initialized successfully');
        } catch (error) {
          console.error('Error initializing autocomplete:', error);
          // Try again after a delay
          setTimeout(checkGoogleReady, 1000);
        }
      } else {
        // Not ready yet, try again
        setTimeout(checkGoogleReady, 100);
      }
    };

    // Start checking after a brief delay
    const timer = setTimeout(checkGoogleReady, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  // Initialize map when moving to step 2
  useEffect(() => {
    if (step !== 2 || !selectedPlace || !window.google || mapInstanceRef.current) return;

    const location = selectedPlace.geometry.location;
    
    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: location.lat(), lng: location.lng() },
      zoom: 20,
      mapTypeId: 'satellite',
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
      tilt: 0  // Force straight-down view (no 45-degree angle)
    });

    // Add marker at address
    new window.google.maps.Marker({
      position: { lat: location.lat(), lng: location.lng() },
      map: map,
      title: 'Your Address'
    });

    // Initialize drawing manager
    const drawingManager = new window.google.maps.drawing.DrawingManager({
      drawingMode: null,
      drawingControl: false,
      polygonOptions: {
        fillColor: '#FF6B35',
        fillOpacity: 0.4,
        strokeWeight: 2,
        strokeColor: '#FF6B35',
        editable: true,
        draggable: false
      }
    });

    drawingManager.setMap(map);

    // Handle polygon complete
    window.google.maps.event.addListener(drawingManager, 'polygoncomplete', (poly) => {
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
      }
      
      polygonRef.current = poly;
      drawingManager.setDrawingMode(null);
      setIsDrawing(false);
      setPolygon(poly);
      computeArea(poly);
      
      logEvent('polygon_completed', { area: computeAreaValue(poly) });

      // Listen for path changes
      window.google.maps.event.addListener(poly.getPath(), 'set_at', () => computeArea(poly));
      window.google.maps.event.addListener(poly.getPath(), 'insert_at', () => computeArea(poly));
      window.google.maps.event.addListener(poly.getPath(), 'remove_at', () => computeArea(poly));
    });

    mapInstanceRef.current = map;
    drawingManagerRef.current = drawingManager;
    setMapLoaded(true);
    
    logEvent('map_loaded', { address: selectedPlace.formatted_address });
  }, [step, selectedPlace]);

  const computeAreaValue = (poly) => {
    if (!poly || !window.google) return 0;
    const areaSqMeters = window.google.maps.geometry.spherical.computeArea(poly.getPath());
    return areaSqMeters * 10.7639; // Convert to sq ft
  };

  const computeArea = (poly) => {
    const sqFt = computeAreaValue(poly);
    setArea(sqFt);
  };

  const startDrawing = () => {
    if (drawingManagerRef.current) {
      drawingManagerRef.current.setDrawingMode(window.google.maps.drawing.OverlayType.POLYGON);
      setIsDrawing(true);
      logEvent('polygon_started');
    }
  };

  const clearPolygon = () => {
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
      setPolygon(null);
      setArea(0);
    }
  };

  const handleNextStep = () => {
    if (step === 1 && !selectedPlace) {
      alert('Please select an address');
      return;
    }
    if (step === 2 && !polygon) {
      alert('Please trace your driveway on the map');
      return;
    }
    setStep(step + 1);
  };

  const handleReset = () => {
    setStep(1);
    setAddress('');
    setSelectedPlace(null);
    setZipCode('');
    setManualZip('');
    setPolygon(null);
    setArea(0);
    setQuote(null);
    setShowLeadForm(false);
    setLeadSubmitted(false);
    setThickness('4');
    setReinforcement('none');
    setFinish('broom');
    setTint('none');
    setCosmetics({});
    setLeadForm({ name: '', phone: '', email: '', notes: '', timeline: 'asap' });
    
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }
    if (mapInstanceRef.current) {
      mapInstanceRef.current = null;
    }
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
  };

  const toggleCosmetic = (key) => {
    setCosmetics(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Calculate quote whenever inputs change
  useEffect(() => {
    if (area > 0 && step >= 3) {
      const effectiveZip = manualZip || zipCode;
      const quoteData = calculateQuote({
        area,
        thickness: parseInt(thickness),
        reinforcement,
        finish,
        tint,
        cosmetics,
        zip: effectiveZip
      }, pricingConfig);
      
      setQuote(quoteData);
      logEvent('quote_rendered', { total: quoteData.total, area });
    }
  }, [area, thickness, reinforcement, finish, tint, cosmetics, manualZip, zipCode, step]);

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    
    if (!leadForm.name || !leadForm.email || !leadForm.phone) {
      alert('Please fill in all required fields');
      return;
    }

    const polygonPath = polygon.getPath().getArray().map(latLng => ({
      lat: latLng.lat(),
      lng: latLng.lng()
    }));

    // Format selected add-ons as a readable list
    const selectedAddOns = [];
    Object.keys(cosmetics).forEach(key => {
      if (cosmetics[key] && pricingConfig.cosmeticAdders[key]) {
        selectedAddOns.push(pricingConfig.cosmeticAdders[key].label);
      }
    });

    const payload = {
      address,
      lat: selectedPlace.geometry.location.lat(),
      lng: selectedPlace.geometry.location.lng(),
      zip: manualZip || zipCode,
      polygonPath,
      area,
      options: {
        thickness: thickness + '"',
        reinforcement,
        finish,
        tint,
        cosmetics,
        addOnsSelected: selectedAddOns.length > 0 ? selectedAddOns : ['None']
      },
      quote: quote,
      lead: leadForm,
      timestamp: new Date().toISOString()
    };

    try {
      // Log the payload for debugging
      console.log('Submitting lead:', payload);
      
      const response = await fetch('/api/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setLeadSubmitted(true);
        logEvent('lead_submitted', { total: quote.total });
      } else {
        // In development, still show success for testing UI
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          console.warn('API endpoint not available in development - showing success for testing');
          console.log('Lead data that would be sent:', payload);
          setLeadSubmitted(true);
          logEvent('lead_submitted', { total: quote.total });
        } else {
          alert('Failed to submit. Please try again or call us directly.');
        }
      }
    } catch (error) {
      console.error('Lead submission error:', error);
      
      // In development, still show success for testing UI
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        console.warn('API endpoint not available in development - showing success for testing');
        console.log('Lead data that would be sent:', payload);
        setLeadSubmitted(true);
        logEvent('lead_submitted', { total: quote.total });
      } else {
        alert('Failed to submit. Please try again or call us directly.');
      }
    }
  };

  return (
    <div className="app">
      <header className="header">
        <h1>Instant Driveway Quote</h1>
        <p className="tagline">Get your estimate in under 2 minutes</p>
      </header>

      <div className="progress-bar">
        <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>1. Address</div>
        <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>2. Trace Driveway</div>
        <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>3. Options & Quote</div>
      </div>

      <main className="main-content">
        {/* Step 1: Address Input */}
        {step === 1 && (
          <div className="step-content">
            <h2>Enter Your Address</h2>
            <p className="step-description">Start by entering your property address</p>
            
            <div className="address-input-wrapper">
              <input
                ref={searchInputRef}
                type="text"
                className="address-input"
                placeholder="123 Main St, Grand Rapids, MI 49503"
              />
            </div>

            {selectedPlace && (
              <div className="address-confirmation">
                <p><strong>Selected:</strong> {address}</p>
                {!zipCode && (
                  <div className="manual-zip">
                    <label>
                      ZIP Code (required):
                      <input
                        type="text"
                        value={manualZip}
                        onChange={(e) => setManualZip(e.target.value)}
                        placeholder="49503"
                        maxLength="5"
                        pattern="[0-9]{5}"
                      />
                    </label>
                  </div>
                )}
                <button onClick={handleNextStep} className="btn-primary">
                  Next: Trace Driveway →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Map and Tracing */}
        {step === 2 && (
          <div className="step-content">
            <h2>Trace Your Driveway</h2>
            <p className="step-description">
              Click around your driveway to outline it. Click "Finish" when done. You can drag points to adjust.
            </p>

            <div className="map-controls">
              {!polygon && (
                <button 
                  onClick={startDrawing} 
                  className="btn-primary"
                  disabled={isDrawing}
                >
                  {isDrawing ? 'Click to draw...' : 'Start Drawing'}
                </button>
              )}
              {polygon && (
                <>
                  <button onClick={clearPolygon} className="btn-secondary">
                    Clear & Redraw
                  </button>
                  <div className="area-display">
                    <strong>Area:</strong> {area.toFixed(0)} sq ft 
                    ({(area / 10.7639).toFixed(1)} m²)
                  </div>
                </>
              )}
            </div>

            <div ref={mapRef} className="map-container"></div>

            {polygon && area > 0 && (
              <div className="map-footer">
                {area < 200 && (
                  <p className="warning">⚠️ Area seems small for a driveway. Please verify your tracing.</p>
                )}
                {area > 5000 && (
                  <p className="warning">⚠️ Area seems large for a typical driveway. Please verify your tracing.</p>
                )}
                <button onClick={handleNextStep} className="btn-primary">
                  Next: Get Quote →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Options and Quote */}
        {step === 3 && (
          <div className="step-content">
            <div className="quote-layout">
              <div className="options-panel">
                <h2>Customize Your Driveway</h2>

                <div className="option-group">
                  <label className="option-label">
                    <span className="label-text">Thickness</span>
                    <select 
                      value={thickness} 
                      onChange={(e) => setThickness(e.target.value)}
                      className="option-select"
                    >
                      <option value="4">4 inches (standard residential)</option>
                      <option value="5">5 inches (heavier vehicles)</option>
                      <option value="6">6 inches (commercial grade)</option>
                    </select>
                  </label>
                </div>

                <div className="option-group">
                  <label className="option-label">
                    <span className="label-text">Reinforcement</span>
                    <select 
                      value={reinforcement} 
                      onChange={(e) => setReinforcement(e.target.value)}
                      className="option-select"
                    >
                      <option value="none">None</option>
                      <option value="fiber">Fiber Mesh</option>
                      <option value="rebar">Rebar Grid</option>
                      <option value="both">Fiber + Rebar</option>
                    </select>
                  </label>
                </div>

                <div className="option-group">
                  <label className="option-label">
                    <span className="label-text">Finish/Texture</span>
                    <select 
                      value={finish} 
                      onChange={(e) => setFinish(e.target.value)}
                      className="option-select"
                    >
                      <option value="broom">Broom Finish (standard)</option>
                      <option value="exposed">Exposed Aggregate</option>
                      <option value="stamped">Stamped Pattern</option>
                      <option value="smooth">Smooth Trowel</option>
                    </select>
                  </label>
                </div>

                <div className="option-group">
                  <label className="option-label">
                    <span className="label-text">Color/Tint</span>
                    <select 
                      value={tint} 
                      onChange={(e) => setTint(e.target.value)}
                      className="option-select"
                    >
                      <option value="none">Natural Gray</option>
                      <option value="integral">Integral Color</option>
                      <option value="stain">Stain/Sealer Tint</option>
                    </select>
                  </label>
                </div>

                <div className="option-group">
                  <span className="label-text">Add-Ons (optional)</span>
                  {Object.entries(pricingConfig.cosmeticAdders).map(([key, adder]) => (
                    <label key={key} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={cosmetics[key] || false}
                        onChange={() => toggleCosmetic(key)}
                      />
                      <span>{adder.label}</span>
                    </label>
                  ))}
                </div>

                {!zipCode && (
                  <div className="option-group">
                    <label className="option-label">
                      <span className="label-text">ZIP Code</span>
                      <input
                        type="text"
                        value={manualZip}
                        onChange={(e) => setManualZip(e.target.value)}
                        placeholder="49503"
                        maxLength="5"
                        pattern="[0-9]{5}"
                        className="zip-input"
                      />
                    </label>
                  </div>
                )}
              </div>

              <div className="quote-panel">
                {quote && (
                  <>
                    <h2>Your Instant Estimate</h2>
                    
                    <div className="quote-total">
                      {quote.showRange ? (
                        <>
                          <div className="price-range">
                            ${quote.low.toLocaleString()} - ${quote.high.toLocaleString()}
                          </div>
                          <div className="price-midpoint">
                            Midpoint: ${quote.total.toLocaleString()}
                          </div>
                        </>
                      ) : (
                        <div className="price-single">
                          ${quote.total.toLocaleString()}
                        </div>
                      )}
                    </div>

                    <div className="quote-breakdown">
                      <h3>Breakdown</h3>
                      {quote.breakdown.map((item, idx) => (
                        <div key={idx} className="breakdown-item">
                          <span>{item.label}</span>
                          <span>${item.value.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>

                    <div className="quote-disclaimer">
                      <p>
                        <strong>Online estimate only.</strong> Final quote provided after onsite verification.
                      </p>
                      <p className="assumptions">
                        Assumes: Level ground, accessible site, standard prep. 
                        {quote.minimumApplied && ' Minimum job charge applied.'}
                      </p>
                    </div>

                    {!showLeadForm && !leadSubmitted && (
                      <button 
                        onClick={() => setShowLeadForm(true)} 
                        className="btn-primary btn-large"
                      >
                        Get Official Quote →
                      </button>
                    )}

                    {showLeadForm && !leadSubmitted && (
                      <div className="lead-form">
                        <h3>Request Official Quote</h3>
                        <form onSubmit={handleLeadSubmit}>
                          <input
                            type="text"
                            placeholder="Full Name *"
                            value={leadForm.name}
                            onChange={(e) => setLeadForm({...leadForm, name: e.target.value})}
                            required
                          />
                          <input
                            type="tel"
                            placeholder="Phone Number *"
                            value={leadForm.phone}
                            onChange={(e) => setLeadForm({...leadForm, phone: e.target.value})}
                            required
                          />
                          <input
                            type="email"
                            placeholder="Email Address *"
                            value={leadForm.email}
                            onChange={(e) => setLeadForm({...leadForm, email: e.target.value})}
                            required
                          />
                          <select
                            value={leadForm.timeline}
                            onChange={(e) => setLeadForm({...leadForm, timeline: e.target.value})}
                          >
                            <option value="asap">As soon as possible</option>
                            <option value="1-2weeks">1-2 weeks</option>
                            <option value="1month">Within a month</option>
                            <option value="3months">Within 3 months</option>
                            <option value="planning">Just planning</option>
                          </select>
                          <textarea
                            placeholder="Additional notes (optional)"
                            value={leadForm.notes}
                            onChange={(e) => setLeadForm({...leadForm, notes: e.target.value})}
                            rows="3"
                          />
                          <button type="submit" className="btn-primary">
                            Submit Request
                          </button>
                        </form>
                      </div>
                    )}

                    {leadSubmitted && (
                      <div className="lead-success">
                        <h3>✓ Request Submitted!</h3>
                        <p>
                          Thank you, {leadForm.name}! We'll contact you within 24 hours to 
                          schedule an onsite visit and provide your official quote.
                        </p>
                        <p className="summary">
                          <strong>Your Estimate:</strong> ${quote.total.toLocaleString()}<br/>
                          <strong>Area:</strong> {area.toFixed(0)} sq ft<br/>
                          <strong>Address:</strong> {address}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="footer">
        <button onClick={handleReset} className="btn-reset">
          ← Start Over
        </button>
        <p className="footer-text">
          Questions? Call us at (616) 555-DRIVE
        </p>
      </footer>
    </div>
  );
}

export default App;
